import { transformAmount } from "@circle-react/components/Paywalls/Admin/PaywallsAdmin/FormModal/helpers/paywallCustomizationSerializer";
import {
  isInstallmentsPrice,
  isOneTimePrice,
} from "@circle-react/helpers/paywallPriceHelpers";
import type { CouponApplication } from "@circle-react/types";
import type { ReducedPrice } from "@circle-react/types/PaywallCustomization";

import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";

import { defaultFormatPaywallCurrency } from "../constants";
import type { PriceScenariosProps } from "../types";
import { usePriceProviderContext } from "./PriceProvider";

// Returns a qualifier string when the coupon does not cover all installments,
// or undefined when no qualifier is needed (all installments covered or no coupon).
// installments_covered comes from coupon_applications.application_count on the backend,
// which uses CouponApplicationsCount — the single source of truth for this calculation.
export const getCouponInstallmentQualifier = (
  installments_covered: number | undefined,
  installmentsCount: number,
): string | undefined => {
  if (installments_covered === undefined) {
    return undefined;
  }

  if (installments_covered >= installmentsCount) {
    return undefined;
  }

  if (installments_covered <= 1) {
    return t("paywall_checkout.coupon.installment_qualifier.once");
  }

  return t("paywall_checkout.coupon.installment_qualifier.repeating", {
    count: installments_covered,
  });
};

const getPriceAttributes = (
  originalPrice: number,
  couponApplication?: CouponApplication,
  reducedPrice?: ReducedPrice,
  installmentsCount = 1,
) => {
  const safeInstallmentsCount = Math.max(installmentsCount, 1);
  let totalPrice = originalPrice;
  let totalPriceDiscounted = -1;

  if (couponApplication) {
    totalPrice = totalPrice / safeInstallmentsCount;

    // Coupon does not return the total amount discount for installments
    totalPriceDiscounted = Math.max(
      Math.floor(totalPrice - (couponApplication?.discount_amount || 0)),
      0,
    );
  } else if (
    reducedPrice &&
    transformAmount(reducedPrice.amount) > originalPrice
  ) {
    // When we apply the reduced Price
    totalPrice = transformAmount(reducedPrice.amount) / safeInstallmentsCount;
    totalPriceDiscounted = originalPrice / safeInstallmentsCount;
  } else {
    // No discounts applied
    totalPrice /= safeInstallmentsCount;
  }

  return {
    totalPrice,
    totalPriceDiscounted,
  };
};

const getTitleAndOldTitle = (
  totalPrice: number,
  paywall: PriceScenariosProps["paywall"],
  totalPriceDiscounted: number,
) => {
  let oldTitle;
  let title;

  if (totalPriceDiscounted >= 0) {
    oldTitle = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPriceDiscounted,
      defaultFormatPaywallCurrency,
    );
  } else {
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
  }

  return {
    title,
    oldTitle,
  };
};

const getSubscriptionPrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps) => {
  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    price.amount,
    couponApplication,
    reducedPrice,
  );

  return {
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getInstallmentPrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps): {
  prefix: string;
  title: string;
  oldTitle?: string;
  suffix: string;
} | null => {
  if (!isInstallmentsPrice(price)) {
    return null;
  }

  const totalAmount = Math.floor(
    price.amount - (price.upfront_payment_amount || 0),
  );

  // We only need to render the first installment, so we need to pass
  // only the installment amount, and not the total.
  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    totalAmount,
    couponApplication,
    reducedPrice,
    price.installments_count || 1,
  );

  return {
    prefix: `${price.installments_count}x`,
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getOneTimePrice = ({
  price,
  paywall,
  couponApplication,
  reducedPrice,
}: PriceScenariosProps) => {
  if (!isOneTimePrice(price)) {
    return null;
  }

  const { totalPrice, totalPriceDiscounted } = getPriceAttributes(
    price.amount,
    couponApplication,
    reducedPrice,
  );

  return {
    ...getTitleAndOldTitle(totalPrice, paywall, totalPriceDiscounted),
  };
};

export const Value = (props: PriceScenariosProps) => {
  const { Title } = usePriceProviderContext();
  const installmentPrice = getInstallmentPrice(props);

  if (installmentPrice) {
    return <Title {...installmentPrice} />;
  }

  return <Title {...(getOneTimePrice(props) || getSubscriptionPrice(props))} />;
};
